import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ProfileEditor } from "./MyProfile";
import { Search, ArrowLeft, UserSquare2 } from "lucide-react";

export default function Profiles() {
  const { consultantId } = useParams();
  const [consultants, setConsultants] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ expertise: "", grade_level: "", service_area: "" });

  const loadList = () => {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    api.get(`/admin/profiles?${q}`).then((r) => setConsultants(r.data));
  };
  useEffect(() => { loadList(); }, [filters]);
  const loadOne = () => consultantId && api.get(`/admin/profiles/${consultantId}`).then((r) => setSelected(r.data));
  useEffect(() => { loadOne(); }, [consultantId]);

  if (consultantId) {
    if (!selected) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="profile-loading">Loading consultant…</div>;
    return (
      <div className="space-y-6" data-testid="admin-profile-detail">
        <div className="fade-up">
          <Link to="/profiles" className="text-sm font-bold text-[#78716C] hover:text-navy flex items-center gap-1 mb-3" data-testid="back-to-profiles-link">
            <ArrowLeft size={14} /> All consultants</Link>
          <p className="eyebrow">Admin review</p>
          <h1 className="section-title text-3xl mt-1">{selected.name}</h1>
          <p className="mt-3 text-sm text-[#57534E] max-w-2xl">
            Review and approve materials for proposal use. Approvals freeze a snapshot — later consultant edits
            are flagged for review and never overwrite the approved version.
          </p>
        </div>
        <ProfileEditor profile={selected} isAdminView onChange={loadOne} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profiles-page">
      <div className="fade-up">
        <p className="eyebrow">Admin only</p>
        <h1 className="section-title text-3xl mt-1">Consultant Profiles & Résumés</h1>
        <p className="mt-3 text-sm text-[#57534E] max-w-2xl">
          Searchable directory of consultant qualifications and approved materials for staffing and proposals.
          Filtering never infers availability and never assigns work automatically.
        </p>
      </div>
      <div className="flex flex-wrap gap-3 fade-up-1" data-testid="profile-filters">
        {[["expertise", "Filter by expertise…"], ["grade_level", "Grade level…"], ["service_area", "Service area…"]].map(([k, ph]) => (
          <div key={k} className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <input className="input !pl-8 max-w-[220px]" placeholder={ph} value={filters[k]} data-testid={`profile-filter-${k}`}
              onChange={(e) => setFilters({ ...filters, [k]: e.target.value })} />
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 fade-up-2" data-testid="profiles-grid">
        {(consultants || []).map((c) => {
          const p = c.profile || {};
          const appr = c.profile_approved || {};
          return (
            <Link key={c.id} to={`/profiles/${c.id}`} className="card card-hover p-5 block" data-testid={`profile-card-${c.id}`}>
              <div className="flex items-center gap-3">
                {p.headshot_path
                  ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profiles/${c.id}/headshot`} alt="" className="w-12 h-12 rounded-full object-cover border border-[#E7E1D4]" />
                  : <div className="w-12 h-12 rounded-full bg-sand flex items-center justify-center text-terracotta"><UserSquare2 size={20} /></div>}
                <div className="min-w-0">
                  <div className="font-bold text-navy text-sm">{c.name}</div>
                  <div className="text-xs text-[#78716C] truncate">{p.professional_title || c.email}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(p.expertise || []).slice(0, 3).map((e) => <span key={e} className="badge-neutral">{e}</span>)}
              </div>
              <div className="flex gap-1.5 mt-3 text-[10px]">
                <span className={appr.short_bio || appr.full_bio ? "badge-approved" : "badge-neutral"}>Bio {appr.short_bio || appr.full_bio ? "✓" : "—"}</span>
                <span className={appr.resume_version_id ? "badge-approved" : "badge-neutral"}>Résumé {appr.resume_version_id ? "✓" : "—"}</span>
                <span className={appr.headshot_path ? "badge-approved" : "badge-neutral"}>Headshot {appr.headshot_path ? "✓" : "—"}</span>
              </div>
            </Link>
          );
        })}
        {consultants && consultants.length === 0 && <p className="text-sm text-[#78716C]" data-testid="profiles-empty">No consultants match the filters.</p>}
      </div>
    </div>
  );
}
